import { PAGINATED_SIZE } from '@application/components/controllers/pages/BreederListPageController/constants';
import { isPromoItem, useRouter, useSkipOnFirstRender } from '@application/utils';
import {
  BreedOverview,
  Button,
  CardItemBreeder,
  CardItemContainer,
  CardItemLitter,
  CardItemWrapper,
  Container,
  EmptyOverlay,
  FiltersWithModal,
  HeaderSearch,
  ListingQuizzCta,
  LoadMoreWrapper,
  Pagination,
  PaginationWrapper,
  StoreCardItemPromo,
} from '@presentation/components';
import type { StoreCardItemPromoData } from '@presentation/components';
import type { FC } from 'react';
import type { BreederListPageProps } from '..';
import { Filters } from './components/Filters';
import * as Styled from './styled';

export * from './types';

export const BreederListPage: FC<BreederListPageProps> = (props) => {
  const {
    items,
    pagination,
    formOptions,
    litterCategories,
    loading = false,
    littersLoading,
    breedOverview,
    filtersModal,
    pageTitle,
    breedInteretBanner,
  } = props;

  const { translate } = useRouter();
  const canRender = useSkipOnFirstRender();

  return (
    <>
      <HeaderSearch title={pageTitle} />

      <Styled.Flipped>
        {breedOverview && (
          <Styled.OverviewContainer>
            <BreedOverview {...breedOverview} />
          </Styled.OverviewContainer>
        )}

        {canRender && (
          <Styled.FilterContainer>
            <FiltersWithModal {...filtersModal}>
              <Filters formOptions={formOptions} />
            </FiltersWithModal>
          </Styled.FilterContainer>
        )}
      </Styled.Flipped>

      <Container>
        <ListingQuizzCta />

        {!loading && !items?.length && (
          <EmptyOverlay wrapperProps={{ style: { minHeight: '200px' } }} />
        )}
        <>
          <CardItemContainer>
            {!!loading &&
              Array(PAGINATED_SIZE)
                .fill(undefined)
                .map((_, index) => (
                  <CardItemWrapper key={index}>
                    <CardItemBreeder loading />
                  </CardItemWrapper>
                ))}

            {!loading &&
              items.map((item, index) => (
                <CardItemWrapper key={index}>
                  {isPromoItem(index, PAGINATED_SIZE) ? (
                    <StoreCardItemPromo {...(item as StoreCardItemPromoData)} />
                  ) : (
                    <CardItemBreeder {...item} useHeadingForTitle={!breedOverview} />
                  )}
                </CardItemWrapper>
              ))}
          </CardItemContainer>
          <PaginationWrapper>
            <Pagination marginPagesDisplayed={1} pageRangeDisplayed={2} {...pagination} />
          </PaginationWrapper>
        </>
      </Container>

      {!littersLoading && !!litterCategories?.length ? (
        <>
          {litterCategories.map((category, index) => (
            <Styled.SectionWrapperLitters key={index}>
              <Container>
                <Styled.SectionTitle color="blue">
                  {translate('common.sections.breedLitters').replace('$breed', category.label)}
                </Styled.SectionTitle>
                <CardItemContainer>
                  {(category.litters ?? []).map((litter, litterIndex) => (
                    <CardItemWrapper key={litterIndex}>
                      <CardItemLitter {...litter} useHeadingForTitle={!breedOverview} />
                    </CardItemWrapper>
                  ))}
                </CardItemContainer>
                {category.canLoadMoreRelatedLitters && (
                  <LoadMoreWrapper>
                    <Button shrink outlined color="blue" link={category.link}>
                      {translate('common.buttons.seeMore')}
                    </Button>
                  </LoadMoreWrapper>
                )}
              </Container>
            </Styled.SectionWrapperLitters>
          ))}
        </>
      ) : null}

      {breedInteretBanner && (
        <Styled.BreedInterestBannerSection>
          <Container>{breedInteretBanner}</Container>
        </Styled.BreedInterestBannerSection>
      )}
    </>
  );
};
